import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User, FreestyleRoom, FreestyleCompetitor, FreestyleScore, FreestyleMatch } from '../types';
import { fetchRoom, updateRoom, submitScoresBatch, calculatePrelimResults as calculatePrelimResultsApi, submitVote } from '../api_fs';
import { ArrowLeft, Users, Trophy, Check, AlertCircle, RefreshCw, X, ArrowRight, ListPlus, FileSpreadsheet } from 'lucide-react';
import { useI18n } from '../i18n';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';

interface FreestyleRoomProps {
  user: User;
}

interface FreestyleScoreDraft {
  style: number;
  completeness: number;
  difficulty: number;
  dirty: boolean;
}

const FREESTYLE_SCORE_DRAFT_STORAGE_PREFIX = 'kendama-freestyle-score-drafts';

const getFreestyleScoreDraftStorageKey = (roomId: string, userId: string) => (
  `${FREESTYLE_SCORE_DRAFT_STORAGE_PREFIX}:${roomId}:${userId}`
);

const normalizeStoredDraftValue = (value: unknown) => (
  typeof value === 'number' && Number.isFinite(value) ? value : 0
);

const sanitizeStoredFreestyleScoreDraft = (value: unknown): FreestyleScoreDraft | null => {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Partial<FreestyleScoreDraft>;
  return {
    style: normalizeStoredDraftValue(candidate.style),
    completeness: normalizeStoredDraftValue(candidate.completeness),
    difficulty: normalizeStoredDraftValue(candidate.difficulty),
    dirty: Boolean(candidate.dirty)
  };
};

const readStoredFreestyleScoreDrafts = (roomId: string, userId: string): Record<string, FreestyleScoreDraft> => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(getFreestyleScoreDraftStorageKey(roomId, userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return Object.entries(parsed).reduce<Record<string, FreestyleScoreDraft>>((acc, [competitorId, draft]) => {
      const sanitizedDraft = sanitizeStoredFreestyleScoreDraft(draft);
      if (sanitizedDraft?.dirty) {
        acc[competitorId] = sanitizedDraft;
      }
      return acc;
    }, {});
  } catch (error) {
    console.warn('Failed to restore freestyle score drafts:', error);
    return {};
  }
};

const writeStoredFreestyleScoreDrafts = (
  roomId: string,
  userId: string,
  drafts: Record<string, FreestyleScoreDraft>
) => {
  if (typeof window === 'undefined') return;
  const storageKey = getFreestyleScoreDraftStorageKey(roomId, userId);
  const entries = Object.entries(drafts).filter(([, draft]) => draft?.dirty);
  if (entries.length === 0) {
    window.localStorage.removeItem(storageKey);
    return;
  }
  window.localStorage.setItem(storageKey, JSON.stringify(Object.fromEntries(entries)));
};

const clearStoredFreestyleScoreDrafts = (roomId: string, userId: string) => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(getFreestyleScoreDraftStorageKey(roomId, userId));
};

export const FreestyleRoomComponent: React.FC<FreestyleRoomProps> = ({ user }) => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [room, setRoom] = useState<FreestyleRoom | null>(null);
  const [loading, setLoading] = useState(true);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [showBatchInput, setShowBatchInput] = useState(false);
  const [batchNames, setBatchNames] = useState('');
  const [selectedCompetitorIds, setSelectedCompetitorIds] = useState<string[]>([]);
  const [finalsAssignments, setFinalsAssignments] = useState<string[]>(Array(16).fill(''));
  const [showFinalsSetupScreen, setShowFinalsSetupScreen] = useState(false);
  const [selectedFinalsSlotIndex, setSelectedFinalsSlotIndex] = useState<number | null>(null);
  const [isRefreshingRoom, setIsRefreshingRoom] = useState(false);
  const [isSubmittingScoreSheet, setIsSubmittingScoreSheet] = useState(false);
  const [isCalculatingPrelims, setIsCalculatingPrelims] = useState(false);
  const [roomLoadErrorMessage, setRoomLoadErrorMessage] = useState<string | null>(null);
  const [areJudgeDraftsHydrated, setAreJudgeDraftsHydrated] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const finalsDetailsRef = useRef<HTMLDivElement>(null);
  const latestLoadRequestIdRef = useRef(0);
  const latestAppliedLoadRequestIdRef = useRef(0);
  const latestAppliedRoomRevisionRef = useRef(0);
  const isRoomLoadInFlightRef = useRef(false);

  // Prelim State
  const [currentPlayerId, setCurrentPlayerId] = useState<string | null>(null);
  const [judgeScoreDrafts, setJudgeScoreDrafts] = useState<Record<string, FreestyleScoreDraft>>({});

  const isOwner = room?.ownerId === user.uid;
  const isJudge = Boolean(room && user?.uid);
  const createDefaultScoreDraft = (): FreestyleScoreDraft => ({
    style: 0,
    completeness: 0,
    difficulty: 0,
    dirty: false
  });
  const getDraftFromScore = (score?: FreestyleScore): FreestyleScoreDraft => ({
    style: score?.style ?? 0,
    completeness: score?.completeness ?? 0,
    difficulty: score?.difficulty ?? 0,
    dirty: false
  });
  const hasCalculatedPrelimResults = Boolean(room?.prelimResultsCalculatedAt);

  const getStableRandomOrderValue = (seed: string) => {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
    }
    return Math.abs(hash);
  };
  const getJudgePanelCompetitors = (sourceRoom: FreestyleRoom | null = room) => {
    if (!sourceRoom) return [];
    return calculatePrelimResults(sourceRoom);
  };

  const calculatePrelimResults = (sourceRoom: FreestyleRoom | null = room) => {
    if (!sourceRoom) return [];
    const totalJudges = sourceRoom.judges.length;
    const hasCalculatedResults = Boolean(sourceRoom.prelimResultsCalculatedAt);

    const comps = sourceRoom.competitors.map((c, index) => {
      const scores = Object.values(c.prelimScores || {}) as FreestyleScore[];
      const scoreCount = scores.length;
      const avg = hasCalculatedResults
        ? (typeof c.prelimAverage === 'number' && Number.isFinite(c.prelimAverage) ? c.prelimAverage : 0)
        : 0;
      const avgCompleteness = hasCalculatedResults && scoreCount > 0 ? scores.reduce((sum, s) => sum + s.completeness, 0) / scoreCount : 0;
      const avgDifficulty = hasCalculatedResults && scoreCount > 0 ? scores.reduce((sum, s) => sum + s.difficulty, 0) / scoreCount : 0;
      const avgStyle = hasCalculatedResults && scoreCount > 0 ? scores.reduce((sum, s) => sum + s.style, 0) / scoreCount : 0;
      const stableRandomOrder = getStableRandomOrderValue(`${sourceRoom.id}:${c.id}`);
      return {
        ...c,
        prelimAverage: avg,
        _avgCompleteness: avgCompleteness,
        _avgDifficulty: avgDifficulty,
        _avgStyle: avgStyle,
        _stableRandomOrder: stableRandomOrder,
        _originalIndex: index,
        _scoreCount: scoreCount,
        _pendingJudgeCount: Math.max(totalJudges - scoreCount, 0),
        _isScoreComplete: totalJudges > 0 && scoreCount >= totalJudges
      };
    });

    if (hasCalculatedResults) {
      comps.sort((a, b) => {
        if (b.prelimAverage !== a.prelimAverage) return b.prelimAverage - a.prelimAverage;
        if (b._avgCompleteness !== a._avgCompleteness) return b._avgCompleteness - a._avgCompleteness;
        if (b._avgDifficulty !== a._avgDifficulty) return b._avgDifficulty - a._avgDifficulty;
        if (b._avgStyle !== a._avgStyle) return b._avgStyle - a._avgStyle;
        return a._stableRandomOrder - b._stableRandomOrder;
      });
      return comps.map((c, i) => ({
        ...c,
        prelimRank: i + 1,
        isFinalist: i < 16
      }));
    }

    return comps.map(c => ({
      ...c,
      prelimRank: undefined,
      isFinalist: false
    }));
  };

  const getJudgeSubmissionProgress = (sourceRoom: FreestyleRoom | null = room) => {
    if (!sourceRoom) {
      return { submittedJudgeCount: 0, totalJudgeCount: 0, allJudgesSubmitted: false };
    }
    const totalJudgeCount = sourceRoom.judges.length;
    const submittedJudgeCount = sourceRoom.judges.filter(judgeId =>
      typeof sourceRoom.prelimJudgeSubmissionStatus?.[judgeId]?.submittedAt === 'number'
    ).length;
    return {
      submittedJudgeCount,
      totalJudgeCount,
      allJudgesSubmitted: totalJudgeCount > 0 && submittedJudgeCount >= totalJudgeCount
    };
  };
  const judgePanelCompetitors = getJudgePanelCompetitors();

  const buildDefaultFinalsAssignments = (finalists: FreestyleCompetitor[]) => {
    const defaultSeedOrder = [0, 14, 2, 12, 4, 10, 6, 8, 1, 15, 3, 13, 5, 11, 7, 9];
    return defaultSeedOrder.map(index => finalists[index]?.id || '');
  };

  const getSafeRoomFileName = () => (room?.name || 'freestyle_room').replace(/[\\/:*?"<>|]/g, '_');
  const getAlphabetSuffix = (index: number) => {
    let value = index;
    let suffix = '';

    while (value > 0) {
      value -= 1;
      suffix = String.fromCharCode(65 + (value % 26)) + suffix;
      value = Math.floor(value / 26);
    }

    return suffix;
  };

  const getFinalsPlacements = (sourceRoom: FreestyleRoom | null) => {
    if (!sourceRoom) {
      return {
        champion: null,
        runnerUp: null,
        thirdPlace: null,
        fourthPlace: null
      };
    }

    const championshipMatch = sourceRoom.matches.find(m => m.round === 4 && m.position === 0);
    const thirdPlaceMatch = sourceRoom.matches.find(m => m.round === 4 && m.position === 1);

    return {
      champion: sourceRoom.competitors.find(c => c.id === championshipMatch?.winnerId) || null,
      runnerUp: sourceRoom.competitors.find(c => c.id && championshipMatch && [championshipMatch.competitorAId, championshipMatch.competitorBId].includes(c.id) && c.id !== championshipMatch.winnerId) || null,
      thirdPlace: sourceRoom.competitors.find(c => c.id === thirdPlaceMatch?.winnerId) || null,
      fourthPlace: sourceRoom.competitors.find(c => c.id && thirdPlaceMatch && [thirdPlaceMatch.competitorAId, thirdPlaceMatch.competitorBId].includes(c.id) && c.id !== thirdPlaceMatch.winnerId) || null
    };
  };

  const getFinalsMatchMeta = (match: FreestyleMatch) => {
    if (match.round === 1) {
      return {
        group: match.position < 4 ? 'A' : 'B',
        stage: 'Round of 16 / 十六强'
      };
    }

    if (match.round === 2) {
      return {
        group: match.position < 2 ? 'A' : 'B',
        stage: 'Group Semifinal / 小组半决赛'
      };
    }

    if (match.round === 3) {
      return {
        group: match.position === 0 ? 'A' : 'B',
        stage: 'Group Final / 小组决赛'
      };
    }

    if (match.round === 4 && match.position === 0) {
      return {
        group: 'Final',
        stage: 'Championship / 冠亚军赛'
      };
    }

    return {
      group: 'Placement',
      stage: '3rd Place Match / 季军赛'
    };
  };

  const getFinalsMatchSequence = (match: FreestyleMatch) => {
    if (match.round === 1 && match.position < 4) return match.position;
    if (match.round === 2 && match.position < 2) return 4 + match.position;
    if (match.round === 3 && match.position === 0) return 6;
    if (match.round === 1 && match.position >= 4) return 7 + (match.position - 4);
    if (match.round === 2 && match.position >= 2) return 11 + (match.position - 2);
    if (match.round === 3 && match.position === 1) return 13;
    if (match.round === 4 && match.position === 1) return 14;
    return 15;
  };

  const sortMatchesBySequence = (matches: FreestyleMatch[]) => (
    [...matches].sort((a, b) => getFinalsMatchSequence(a) - getFinalsMatchSequence(b))
  );

  const getMatchLoserId = (match?: Pick<FreestyleMatch, 'winnerId' | 'competitorAId' | 'competitorBId'>) => {
    if (!match.winnerId || !match.competitorAId || !match.competitorBId) return '';
    return match.winnerId === match.competitorAId ? match.competitorBId : match.competitorAId;
  };

  const rebuildFinalsBracket = (
    sourceMatches: FreestyleMatch[],
    winnerOverrides: Record<string, string | undefined> = {}
  ) => {
    const matchMap = new Map(sourceMatches.map(match => [match.id, match]));

    const sanitizeVotes = (votes: Record<string, string> | undefined, competitorAId: string, competitorBId: string) => (
      Object.fromEntries(
        Object.entries(votes || {}).filter(([, votedId]) => votedId === competitorAId || votedId === competitorBId)
      )
    );

    const buildMatch = (
      id: string,
      round: number,
      position: number,
      competitorAId: string,
      competitorBId: string
    ): FreestyleMatch => {
      const existing = matchMap.get(id);
      const nextMatch: FreestyleMatch = {
        id,
        round,
        position,
        competitorAId,
        competitorBId,
        votes: sanitizeVotes(existing?.votes, competitorAId, competitorBId)
      };
      const preferredWinnerId = Object.prototype.hasOwnProperty.call(winnerOverrides, id)
        ? winnerOverrides[id]
        : existing?.winnerId;

      if (
        competitorAId &&
        competitorBId &&
        preferredWinnerId &&
        (preferredWinnerId === competitorAId || preferredWinnerId === competitorBId)
      ) {
        nextMatch.winnerId = preferredWinnerId;
      }

      return nextMatch;
    };

    const rebuiltMatches: FreestyleMatch[] = [];

    for (let position = 0; position < 8; position += 1) {
      const existing = matchMap.get(`r1_p${position}`);
      rebuiltMatches.push(
        buildMatch(
          `r1_p${position}`,
          1,
          position,
          existing?.competitorAId || '',
          existing?.competitorBId || ''
        )
      );
    }

    for (let position = 0; position < 4; position += 1) {
      const sourceA = rebuiltMatches.find(match => match.id === `r1_p${position * 2}`);
      const sourceB = rebuiltMatches.find(match => match.id === `r1_p${position * 2 + 1}`);
      rebuiltMatches.push(
        buildMatch(
          `r2_p${position}`,
          2,
          position,
          sourceA?.winnerId || '',
          sourceB?.winnerId || ''
        )
      );
    }

    for (let position = 0; position < 2; position += 1) {
      const sourceA = rebuiltMatches.find(match => match.id === `r2_p${position * 2}`);
      const sourceB = rebuiltMatches.find(match => match.id === `r2_p${position * 2 + 1}`);
      rebuiltMatches.push(
        buildMatch(
          `r3_p${position}`,
          3,
          position,
          sourceA?.winnerId || '',
          sourceB?.winnerId || ''
        )
      );
    }

    const semifinalA = rebuiltMatches.find(match => match.id === 'r3_p0');
    const semifinalB = rebuiltMatches.find(match => match.id === 'r3_p1');

    rebuiltMatches.push(
      buildMatch(
        'r4_p1',
        4,
        1,
        getMatchLoserId(semifinalA),
        getMatchLoserId(semifinalB)
      )
    );
    rebuiltMatches.push(
      buildMatch(
        'r4_p0',
        4,
        0,
        semifinalA?.winnerId || '',
        semifinalB?.winnerId || ''
      )
    );

    return rebuiltMatches;
  };

  const shouldExitRoomOnLoadError = (error: unknown) => {
    const status = typeof (error as { status?: unknown })?.status === 'number'
      ? Number((error as { status?: number }).status)
      : null;
    return status === 401 || status === 403 || status === 404;
  };

  const loadRoom = async () => {
    if (!roomId) return;
    if (isRoomLoadInFlightRef.current) return;

    const requestId = latestLoadRequestIdRef.current + 1;
    latestLoadRequestIdRef.current = requestId;
    isRoomLoadInFlightRef.current = true;
    try {
      const data = await fetchRoom(roomId);
      const nextRoom = data.room as FreestyleRoom;
      const nextRevision = typeof nextRoom?.revision === 'number' ? nextRoom.revision : 0;
      const lastRevision = latestAppliedRoomRevisionRef.current;
      const lastRequestId = latestAppliedLoadRequestIdRef.current;

      if (nextRevision < lastRevision) {
        return;
      }
      if (nextRevision === lastRevision && requestId < lastRequestId) {
        return;
      }

      latestAppliedRoomRevisionRef.current = nextRevision;
      latestAppliedLoadRequestIdRef.current = requestId;
      setRoom(nextRoom);
      setRoomLoadErrorMessage(null);
    } catch (e) {
      if (requestId !== latestLoadRequestIdRef.current) {
        return;
      }
      console.error(e);
      if (shouldExitRoomOnLoadError(e)) {
        navigate('/freestyle');
        return;
      }
      setRoomLoadErrorMessage('房间连接暂时不稳定，系统正在自动重试。请留在当前页面，不要重复退出重进。');
    } finally {
      isRoomLoadInFlightRef.current = false;
      setLoading(false);
    }
  };

  const handleManualRoomRefresh = async () => {
    setIsRefreshingRoom(true);
    try {
      await loadRoom();
    } finally {
      setIsRefreshingRoom(false);
    }
  };

  useEffect(() => {
    loadRoom();
    const interval = window.setInterval(() => {
      if (document.hidden) return;
      loadRoom();
    }, 5000);
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadRoom();
      }
    };
    const handlePageShow = () => {
      loadRoom();
    };
    const handleWindowFocus = () => {
      loadRoom();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', handleWindowFocus);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [roomId, user.uid]);

  useEffect(() => {
    if (!room) return;
    setSelectedCompetitorIds(prev => prev.filter(id => room.competitors.some(c => c.id === id)));
  }, [room]);

  useEffect(() => {
    setAreJudgeDraftsHydrated(false);
  }, [roomId, user.uid]);

  useEffect(() => {
    if (!room) return;
    const storedDrafts = roomId && user.uid && room.status === 'PRELIM'
      ? readStoredFreestyleScoreDrafts(roomId, user.uid)
      : {};
    setJudgeScoreDrafts(prev => {
      const next: Record<string, FreestyleScoreDraft> = {};
      room.competitors.forEach(competitor => {
        const savedDraft = getDraftFromScore(competitor.prelimScores?.[user.uid]);
        const previousDraft = prev[competitor.id];
        const storedDraft = storedDrafts[competitor.id];
        next[competitor.id] = previousDraft?.dirty
          ? previousDraft
          : storedDraft?.dirty
            ? storedDraft
            : savedDraft;
      });
      return next;
    });
    setCurrentPlayerId(prev => (prev && room.competitors.some(c => c.id === prev) ? prev : null));
    setAreJudgeDraftsHydrated(true);
  }, [room, roomId, user.uid]);

  useEffect(() => {
    if (!roomId || !user.uid) return;
    if (!room || !areJudgeDraftsHydrated) return;
    if (room.status !== 'PRELIM') {
      clearStoredFreestyleScoreDrafts(roomId, user.uid);
      return;
    }

    const activeCompetitorIds = new Set(room.competitors.map(competitor => competitor.id));
    const nextStoredDrafts = Object.entries(judgeScoreDrafts).reduce<Record<string, FreestyleScoreDraft>>((acc, [competitorId, draft]) => {
      if (activeCompetitorIds.has(competitorId) && draft?.dirty) {
        acc[competitorId] = draft;
      }
      return acc;
    }, {});

    try {
      writeStoredFreestyleScoreDrafts(roomId, user.uid, nextStoredDrafts);
    } catch (error) {
      console.warn('Failed to persist freestyle score drafts:', error);
    }
  }, [areJudgeDraftsHydrated, judgeScoreDrafts, room, roomId, user.uid]);

  useEffect(() => {
    if (!room || room.status !== 'PRELIM' || !room.prelimResultsCalculatedAt) {
      setFinalsAssignments(Array(16).fill(''));
      return;
    }

    const finalists = calculatePrelimResults(room).filter(c => c.isFinalist);
    if (finalists.length < 16) {
      setFinalsAssignments(Array(16).fill(''));
      return;
    }

    const finalistIds = new Set(finalists.map(c => c.id));
    const defaultAssignments = buildDefaultFinalsAssignments(finalists);

    setFinalsAssignments(prev => {
      const normalizedPrev = Array.from({ length: 16 }, (_, index) => {
        const currentId = prev[index] || '';
        return finalistIds.has(currentId) ? currentId : '';
      });
      const alreadyAssigned = new Set(normalizedPrev.filter(Boolean));
      const remainingIds = defaultAssignments.filter(id => id && !alreadyAssigned.has(id));
      const next = normalizedPrev.map(id => id || remainingIds.shift() || '');
      const prevNormalized = Array.from({ length: 16 }, (_, index) => prev[index] || '');

      return next.every((id, index) => id === prevNormalized[index]) ? prev : next;
    });
  }, [room]);

  useEffect(() => {
    if (room?.status !== 'PRELIM') {
      setShowFinalsSetupScreen(false);
    }
  }, [room?.status]);

  const handleAddPlayer = async () => {
    if (!newPlayerName.trim() || !room || !isOwner) return;
    const newComp: FreestyleCompetitor = {
      id: Date.now().toString(),
      name: newPlayerName.trim(),
      prelimScores: {},
      prelimAverage: 0
    };
    try {
      await updateRoom(room.id, { competitors: [...room.competitors, newComp] });
      setNewPlayerName('');
      loadRoom();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleBatchAdd = async () => {
    if (!batchNames.trim() || !room || !isOwner) return;
    const names = batchNames.split(/[\n,，]+/).map(n => n.trim()).filter(n => n.length > 0);
    if (names.length === 0) return;
    
    const newComps: FreestyleCompetitor[] = names.map((name, i) => ({
      id: Date.now().toString() + '_b_' + i,
      name: name,
      prelimScores: {},
      prelimAverage: 0
    }));
    
    try {
      await updateRoom(room.id, { competitors: [...room.competitors, ...newComps] });
      setBatchNames('');
      setShowBatchInput(false);
      loadRoom();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !room || !isOwner) return;
    
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const ab = evt.target?.result;
        const wb = XLSX.read(ab, { type: 'array' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
        
        const importedPlayers: Array<{ number: string; name: string }> = [];
        data.forEach((row: any) => {
          if (!Array.isArray(row) || row.length < 2) return;

          const playerNumber = String(row[0] ?? '').trim();
          const playerName = String(row[1] ?? '').trim();
          const isHeaderRow =
            ['No', 'NO', 'No.', 'ID', '编号', '选手编号', 'Player No', 'Player Number'].includes(playerNumber) ||
            ['Name', 'name', '姓名', '选手', 'Player', 'player', 'Player Name', '选手姓名'].includes(playerName);

          if (!isHeaderRow && playerName) {
            importedPlayers.push({ number: playerNumber, name: playerName });
          }
        });

        if (importedPlayers.length === 0) {
          alert('No valid player names found in the second column of the Excel file. / 在Excel第二列中未找到有效的选手姓名。');
          return;
        }

        const usedPlayerNumbers = new Set(
          room.competitors
            .map(competitor => String(competitor.playerNumber || '').trim())
            .filter(Boolean)
        );
        const duplicateNumberUsage = new Map<string, number>();
        let nextAutoPlayerNumber = 1;
        const getNextAutoPlayerNumber = () => {
          while (usedPlayerNumbers.has(String(nextAutoPlayerNumber))) {
            nextAutoPlayerNumber += 1;
          }

          const value = String(nextAutoPlayerNumber);
          usedPlayerNumbers.add(value);
          nextAutoPlayerNumber += 1;
          return value;
        };
        const getResolvedPlayerNumber = (rawNumber: string) => {
          const normalizedNumber = rawNumber.trim();
          if (!normalizedNumber) {
            return getNextAutoPlayerNumber();
          }

          if (!usedPlayerNumbers.has(normalizedNumber)) {
            usedPlayerNumbers.add(normalizedNumber);
            return normalizedNumber;
          }

          let currentUsage = duplicateNumberUsage.get(normalizedNumber) || 0;
          let resolvedNumber = normalizedNumber;
          do {
            currentUsage += 1;
            resolvedNumber = `${normalizedNumber}${getAlphabetSuffix(currentUsage)}`;
          } while (usedPlayerNumbers.has(resolvedNumber));

          duplicateNumberUsage.set(normalizedNumber, currentUsage);
          usedPlayerNumbers.add(resolvedNumber);
          return resolvedNumber;
        };
        const importBatchId = Date.now();
        const newComps: FreestyleCompetitor[] = importedPlayers.map((player, i) => {
          const resolvedPlayerNumber = getResolvedPlayerNumber(player.number);
          return {
          id: `${importBatchId}_xl_${i}`,
          name: player.name,
          playerNumber: resolvedPlayerNumber,
          prelimScores: {},
          prelimAverage: 0
          };
        });
        
        await updateRoom(room.id, { competitors: [...room.competitors, ...newComps] });
        loadRoom();
        if (fileInputRef.current) fileInputRef.current.value = '';
      } catch (err: any) {
        alert('Error parsing Excel file / 解析Excel出错: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleRemovePlayer = async (id: string) => {
    if (!room || !isOwner) return;
    try {
      await updateRoom(room.id, { competitors: room.competitors.filter(c => c.id !== id) });
      loadRoom();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleToggleCompetitorSelection = (id: string) => {
    setSelectedCompetitorIds(prev => (
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    ));
  };

  const handleDeleteSelectedPlayers = async () => {
    if (!room || !isOwner || selectedCompetitorIds.length === 0) return;
    const confirmed = window.confirm(
      `Delete ${selectedCompetitorIds.length} selected players? / 删除已选中的 ${selectedCompetitorIds.length} 名选手？`
    );
    if (!confirmed) return;

    try {
      await updateRoom(room.id, {
        competitors: room.competitors.filter(c => !selectedCompetitorIds.includes(c.id))
      });
      setSelectedCompetitorIds([]);
      loadRoom();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleDeleteAllPlayers = async () => {
    if (!room || !isOwner || room.competitors.length === 0) return;
    const confirmed = window.confirm(
      'Delete all players in this room? / 删除该房间内全部选手？'
    );
    if (!confirmed) return;

    try {
      await updateRoom(room.id, { competitors: [] });
      setSelectedCompetitorIds([]);
      loadRoom();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleStartPrelims = async () => {
    if (!room || !isOwner || room.competitors.length === 0) return;
    try {
      await updateRoom(room.id, {
        status: 'PRELIM',
        competitors: room.competitors.map(competitor => ({
          ...competitor,
          prelimScores: {},
          prelimAverage: 0,
          prelimRank: undefined,
          isFinalist: false
        })),
        prelimJudgeSubmissionStatus: {},
        prelimResultsCalculatedAt: undefined,
        matches: []
      });
      loadRoom();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleSelectCompetitorForScoring = (comp: FreestyleCompetitor) => {
    setCurrentPlayerId(comp.id);
  };

  const handleScoreDraftChange = (competitorId: string, key: 'style' | 'completeness' | 'difficulty', value: number) => {
    setJudgeScoreDrafts(prev => ({
      ...prev,
      [competitorId]: {
        ...(prev[competitorId] || createDefaultScoreDraft()),
        [key]: value,
        dirty: true
      }
    }));
  };

  const handleDropToFinalsSlot = (competitorId: string, targetIndex: number) => {
    setFinalsAssignments(prev => {
      const next = Array.from({ length: 16 }, (_, index) => prev[index] || '');
      const sourceIndex = next.findIndex(id => id === competitorId);
      const targetId = next[targetIndex];

      if (sourceIndex === targetIndex) return prev;

      if (sourceIndex !== -1) {
        next[sourceIndex] = targetId || '';
      }

      next[targetIndex] = competitorId;
      return next;
    });
  };

  const handleSelectFinalsSlot = (slotIndex: number) => {
    setFinalsAssignments(prev => {
      const next = Array.from({ length: 16 }, (_, index) => prev[index] || '');
      const currentId = next[slotIndex];

      if (!currentId) {
        setSelectedFinalsSlotIndex(null);
        return prev;
      }

      if (selectedFinalsSlotIndex === null) {
        setSelectedFinalsSlotIndex(slotIndex);
        return prev;
      }

      if (selectedFinalsSlotIndex === slotIndex) {
        setSelectedFinalsSlotIndex(null);
        return prev;
      }

      const selectedId = next[selectedFinalsSlotIndex];
      next[selectedFinalsSlotIndex] = currentId;
      next[slotIndex] = selectedId;
      setSelectedFinalsSlotIndex(null);
      return next;
    });
  };

  const handleAutoFillFinalsAssignments = () => {
    if (!room) return;
    if (!room.prelimResultsCalculatedAt) {
      alert('Please calculate the prelim averages first. / 请先计算初赛平均分。');
      return;
    }
    const finalists = calculatePrelimResults(room).filter(c => c.isFinalist);
    if (finalists.length < 16) return;
    setFinalsAssignments(buildDefaultFinalsAssignments(finalists));
    setSelectedFinalsSlotIndex(null);
  };

  const handleStartFinals = async () => {
    if (!room || !isOwner) return;
    if (!room.prelimResultsCalculatedAt) {
      alert('Please calculate the prelim averages first. / 请先计算初赛平均分。');
      return;
    }
    const rankedComps = calculatePrelimResults();
    const finalists = rankedComps.filter(c => c.isFinalist);
    
    if (finalists.length < 16) {
        alert("Need at least 16 players to generate the freestyle bracket / 至少需要16名选手才能生成此模式的对阵树");
        return;
    }
    
    const assignedIds = finalsAssignments.filter(Boolean);
    if (assignedIds.length < 16 || new Set(assignedIds).size !== 16) {
      alert('Please complete the drag-and-drop finals matchups before starting. / 请先通过拖动完成决赛对阵设置，再开始决赛。');
      return;
    }

    if (assignedIds.some(id => !finalists.some(c => c.id === id))) {
      alert('Finals matchup list is outdated. Please reset it and try again. / 决赛对阵名单已过期，请重新设置后再试。');
      return;
    }

    const matches: any[] = [];
    
    // Round of 16 (8 matches)
    for (let i = 0; i < 8; i++) {
        const aId = finalsAssignments[i * 2];
        const bId = finalsAssignments[i * 2 + 1];
        
        matches.push({
            id: `r1_p${i}`,
            competitorAId: aId,
            competitorBId: bId,
            votes: {},
            round: 1,
            position: i
        });
    }

    try {
      await updateRoom(room.id, { 
        competitors: rankedComps, 
        status: 'FINALS',
        matches 
      });
      setSelectedFinalsSlotIndex(null);
      loadRoom();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleReturnToPrelims = async () => {
    if (!room || !isOwner) return;
    const confirmed = window.confirm(
      'Return to preliminaries and reset the current finals bracket? / 返回初赛并重置当前决赛对阵吗？'
    );
    if (!confirmed) return;

    try {
      await updateRoom(room.id, {
        status: 'PRELIM',
        matches: []
      });
      setCurrentPlayerId(null);
      setShowFinalsSetupScreen(false);
      setSelectedFinalsSlotIndex(null);
      loadRoom();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleGoToRoomLobby = async () => {
    if (!room || !isOwner) return;
    try {
      await updateRoom(room.id, { status: 'CLOSED' });
      loadRoom();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleExportPrelimRankings = () => {
    if (!room) return;
    if (!room.prelimResultsCalculatedAt) {
      alert('Please calculate the prelim averages before exporting rankings. / 请先计算初赛平均分后再导出排名。');
      return;
    }

    const rankedComps = calculatePrelimResults(room);
    const rows = rankedComps.map((comp: any) => ({
      'Rank / 名次': comp.prelimRank,
      'Name / 姓名': comp.name,
      'Average Total / 平均总分': Number(comp.prelimAverage.toFixed(2)),
      'Completeness Avg / 完整度均分': Number((comp._avgCompleteness || 0).toFixed(2)),
      'Difficulty Avg / 难度均分': Number((comp._avgDifficulty || 0).toFixed(2)),
      'Style Avg / 风格均分': Number((comp._avgStyle || 0).toFixed(2))
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet['!cols'] = [
      { wch: 12 },
      { wch: 24 },
      { wch: 18 },
      { wch: 20 },
      { wch: 18 },
      { wch: 18 }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Prelim Rankings');
    XLSX.writeFile(workbook, `${getSafeRoomFileName()}_prelim_rankings.xlsx`);
  };

  const handleExportPrelimDetailedRankings = () => {
    if (!room) return;
    if (!room.prelimResultsCalculatedAt) {
      alert('Please calculate the prelim averages before exporting rankings. / 请先计算初赛平均分后再导出排名。');
      return;
    }

    const rankedComps = calculatePrelimResults(room);
    const submittedJudgeIds = room.judges.filter(judgeId => {
      const submittedAt = room.prelimJudgeSubmissionStatus?.[judgeId]?.submittedAt;
      return Number.isFinite(Number(submittedAt));
    });
    const judgeNameMap = new Map(
      (room.judgeDetails || []).map(judge => [judge.id, judge.name || `Judge ${judge.id}`])
    );

    const summaryRows = rankedComps.map((comp: any) => ({
      'Rank / 名次': comp.prelimRank,
      'No. / 编号': comp.playerNumber || '',
      'Name / 姓名': comp.name,
      'Average Total / 平均总分': Number(comp.prelimAverage.toFixed(2)),
      'Completeness Avg / 完整度均分': Number((comp._avgCompleteness || 0).toFixed(2)),
      'Difficulty Avg / 难度均分': Number((comp._avgDifficulty || 0).toFixed(2)),
      'Style Avg / 风格均分': Number((comp._avgStyle || 0).toFixed(2)),
      'Included Judges / 计入裁判数': comp._scoreCount,
      'Finalist / 晋级决赛': comp.isFinalist ? 'Yes / 是' : 'No / 否'
    }));

    const detailRows = rankedComps.flatMap((comp: any) => {
      const includedJudgeIdSet = new Set(submittedJudgeIds);
      const allJudgeIds = Array.from(new Set([
        ...room.judges,
        ...Object.keys(comp.prelimScores || {})
      ]));

      if (allJudgeIds.length === 0) {
        return [{
          'Rank / 名次': comp.prelimRank,
          'No. / 编号': comp.playerNumber || '',
          'Name / 姓名': comp.name,
          'Judge / 裁判': '',
          'Judge ID / 裁判ID': '',
          'Included In Average / 计入平均分': 'No / 否',
          'Submitted Sheet / 已提交评分表': 'No / 否',
          'Style / 风格': '',
          'Completeness / 完整度': '',
          'Difficulty / 难度': '',
          'Total / 总分': '',
          'Updated At / 更新时间': ''
        }];
      }

      return allJudgeIds.map(judgeId => {
        const score = comp.prelimScores?.[judgeId];
        const submittedAt = room.prelimJudgeSubmissionStatus?.[judgeId]?.submittedAt;
        const updatedAt = score?.updatedAt;
        return {
          'Rank / 名次': comp.prelimRank,
          'No. / 编号': comp.playerNumber || '',
          'Name / 姓名': comp.name,
          'Judge / 裁判': judgeNameMap.get(judgeId) || `Judge ${judgeId}`,
          'Judge ID / 裁判ID': judgeId,
          'Included In Average / 计入平均分': includedJudgeIdSet.has(judgeId) ? 'Yes / 是' : 'No / 否',
          'Submitted Sheet / 已提交评分表': Number.isFinite(Number(submittedAt)) ? 'Yes / 是' : 'No / 否',
          'Style / 风格': typeof score?.style === 'number' ? score.style : '',
          'Completeness / 完整度': typeof score?.completeness === 'number' ? score.completeness : '',
          'Difficulty / 难度': typeof score?.difficulty === 'number' ? score.difficulty : '',
          'Total / 总分': typeof score?.total === 'number' ? score.total : '',
          'Updated At / 更新时间': typeof updatedAt === 'number' && Number.isFinite(updatedAt)
            ? new Date(updatedAt).toLocaleString()
            : ''
        };
      });
    });

    const summarySheet = XLSX.utils.json_to_sheet(summaryRows);
    summarySheet['!cols'] = [
      { wch: 12 },
      { wch: 12 },
      { wch: 24 },
      { wch: 18 },
      { wch: 20 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 16 }
    ];

    const detailSheet = XLSX.utils.json_to_sheet(detailRows);
    detailSheet['!cols'] = [
      { wch: 12 },
      { wch: 12 },
      { wch: 24 },
      { wch: 20 },
      { wch: 18 },
      { wch: 20 },
      { wch: 18 },
      { wch: 14 },
      { wch: 18 },
      { wch: 14 },
      { wch: 14 },
      { wch: 24 }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Prelim Rankings');
    XLSX.utils.book_append_sheet(workbook, detailSheet, 'Prelim Details');
    XLSX.writeFile(workbook, `${getSafeRoomFileName()}_prelim_rankings_detailed.xlsx`);
  };

  const handleExportFinalsDetailsImage = async () => {
    if (!finalsDetailsRef.current || !room) return;

    try {
      const canvas = await html2canvas(finalsDetailsRef.current, {
        backgroundColor: '#050505',
        scale: 2,
        useCORS: true
      });
      const link = document.createElement('a');
      link.download = `${getSafeRoomFileName()}_finals_details.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e: any) {
      alert(e.message || 'Failed to export finals details image / 导出决赛详情图失败');
    }
  };

  const handleScoreSheetSubmit = async () => {
    if (!room || !roomId) return;
    try {
      setIsSubmittingScoreSheet(true);
      const scores = room.competitors.map(competitor => {
        const draft = judgeScoreDrafts[competitor.id]
          || getDraftFromScore(competitor.prelimScores?.[user.uid])
          || createDefaultScoreDraft();
        return {
          competitorId: competitor.id,
          score: {
            judgeId: user.uid,
            style: draft.style,
            completeness: draft.completeness,
            difficulty: draft.difficulty,
            total: draft.style + draft.completeness + draft.difficulty
          }
        };
      });
      const data = await submitScoresBatch(roomId, scores);
      if (data?.room) {
        const submittedRoom = data.room as FreestyleRoom;
        latestAppliedRoomRevisionRef.current = typeof submittedRoom.revision === 'number'
          ? submittedRoom.revision
          : latestAppliedRoomRevisionRef.current;
        latestAppliedLoadRequestIdRef.current = latestLoadRequestIdRef.current;
        setRoom(submittedRoom);
        clearStoredFreestyleScoreDrafts(roomId, user.uid);
        setJudgeScoreDrafts(
          Object.fromEntries(
            submittedRoom.competitors.map(competitor => [
              competitor.id,
              getDraftFromScore(competitor.prelimScores?.[user.uid])
            ])
          )
        );
      }
      setCurrentPlayerId(null);
    } catch (e: any) {
      alert(e.message);
    } finally {
      setIsSubmittingScoreSheet(false);
    }
  };

  const handleCalculatePrelims = async () => {
    if (!room || !roomId || !isOwner) return;
    try {
      setIsCalculatingPrelims(true);
      const data = await calculatePrelimResultsApi(roomId);
      if (data?.room) {
        const nextRoom = data.room as FreestyleRoom;
        latestAppliedRoomRevisionRef.current = typeof nextRoom.revision === 'number'
          ? nextRoom.revision
          : latestAppliedRoomRevisionRef.current;
        latestAppliedLoadRequestIdRef.current = latestLoadRequestIdRef.current;
        setRoom(nextRoom);
      }
      loadRoom();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setIsCalculatingPrelims(false);
    }
  };

  const handleVoteSubmit = async (matchId: string, compId: string) => {
    if (!room || !roomId || !isJudge) return;
    try {
      const data = await submitVote(roomId, matchId, compId);
      if (data?.room) {
        const nextRoom = data.room as FreestyleRoom;
        latestAppliedRoomRevisionRef.current = typeof nextRoom.revision === 'number'
          ? nextRoom.revision
          : latestAppliedRoomRevisionRef.current;
        latestAppliedLoadRequestIdRef.current = latestLoadRequestIdRef.current;
        setRoom(nextRoom);
      }
      loadRoom();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const advanceMatch = async (matchId: string, winnerId: string) => {
      if (!room || !isOwner) return;
      const match = room.matches.find(m => m.id === matchId);
      if (!match) return;

      try {
          await updateRoom(room.id, {
            matches: rebuildFinalsBracket(room.matches, { [matchId]: winnerId })
          });
          loadRoom();
      } catch (e: any) {
          alert(e.message);
      }
  };

  const handleOverrideMatchWinner = async (matchId: string, winnerId: string) => {
    if (!room || !isOwner) return;
    const match = room.matches.find(item => item.id === matchId);
    if (!match) return;

    const winner = room.competitors.find(competitor => competitor.id === winnerId);
    if (!winner) return;

    const confirmed = window.confirm(
      `Change this match winner to ${winner.name}? This will also refresh downstream finals results. / 将这一场的胜者改为 ${winner.name} 吗？系统会同时重算后续决赛结果。`
    );
    if (!confirmed) return;

    try {
      await updateRoom(room.id, {
        matches: rebuildFinalsBracket(room.matches, { [matchId]: winnerId })
      });
      loadRoom();
    } catch (e: any) {
      alert(e.message);
    }
  };

  if (loading) {
    return <div className="h-full w-full flex flex-col items-center justify-center gap-4"><RefreshCw className="animate-spin text-purple-500 w-12 h-12" /><span className="text-zinc-500 font-orbitron">Loading... / 加载中...</span></div>;
  }

  if (!room) {
    return (
      <div className="h-full w-full flex items-center justify-center p-6">
        <div className="w-full max-w-md rounded-2xl border border-red-500/30 bg-zinc-950/90 p-6 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-500/10 text-red-300">
            <AlertCircle size={28} />
          </div>
          <h2 className="text-2xl font-orbitron text-white">Room Unavailable</h2>
          <p className="mt-2 text-sm text-zinc-300">
            {roomLoadErrorMessage || 'Unable to load this room right now. / 当前暂时无法加载该房间。'}
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={handleManualRoomRefresh}
              disabled={isRefreshingRoom}
              className={`flex-1 rounded-xl border px-4 py-3 font-orbitron text-sm transition-colors ${
                isRefreshingRoom
                  ? 'border-cyan-500/20 bg-cyan-500/10 text-cyan-300/50 cursor-not-allowed'
                  : 'border-cyan-500/40 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20'
              }`}
            >
              {isRefreshingRoom ? 'Retrying / 重试中' : 'Retry / 重试'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/freestyle')}
              className="flex-1 rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 font-orbitron text-sm text-zinc-200 transition-colors hover:bg-zinc-800"
            >
              Back To Lobby / 返回大厅
            </button>
          </div>
        </div>
      </div>
    );
  }

  const JudgesList = () => (
    <div className="w-full flex items-center gap-3 bg-zinc-900/50 p-3 rounded-xl border border-zinc-800 mb-6 overflow-x-auto custom-scrollbar">
      <div className="flex items-center gap-2 text-purple-400 font-orbitron whitespace-nowrap">
        <Users size={18} /> Judges / 裁判 ({room.judgeDetails?.length || room.judges.length}):
      </div>
      <div className="flex gap-2">
        {room.judgeDetails?.map(j => (
          <span key={j.id} className="text-sm font-rajdhani text-zinc-300 bg-black/50 px-3 py-1 rounded-full border border-zinc-700 whitespace-nowrap">
            {j.name}
          </span>
        ))}
      </div>
    </div>
  );

  // Render Setup
  if (room.status === 'SETUP') {
    if (!isOwner) {
      return (
        <div className="h-full w-full flex items-center justify-center flex-col gap-4 relative">
          <button onClick={() => navigate('/freestyle')} className="absolute top-4 left-4 p-2 text-white/50 hover:text-white transition-colors"><ArrowLeft size={24} /></button>
          <h2 className="text-3xl font-orbitron text-purple-400 text-center px-4">Waiting for owner to start...<br/><span className="text-xl text-purple-400/80">等待房主开始...</span></h2>
          <p className="text-zinc-400 font-rajdhani text-xl">{room.competitors.length} players registered / 已注册选手</p>
        </div>
      );
    }
    return (
      <div className="h-full w-full overflow-y-auto custom-scrollbar">
        <div className="p-8 pb-10 w-full max-w-4xl mx-auto flex flex-col gap-6 relative min-h-full">
          <button onClick={() => navigate('/freestyle')} className="absolute top-4 left-4 p-2 text-white/50 hover:text-white transition-colors"><ArrowLeft size={24} /></button>
          <h2 className="text-3xl font-orbitron text-purple-400 text-center mt-8">Room: {room.name} - Setup<br/><span className="text-xl text-purple-400/80">房间设置</span></h2>
          
          <JudgesList />

          <div className="flex flex-col gap-4">
            <div className="flex gap-4">
              <input 
                type="text" 
                placeholder="Player Name / 选手姓名" 
                value={newPlayerName} 
                onChange={e => setNewPlayerName(e.target.value)}
                className="flex-1 bg-black/50 border border-zinc-700 rounded p-3 text-white focus:border-purple-500 outline-none"
                onKeyDown={e => e.key === 'Enter' && handleAddPlayer()}
              />
              <button onClick={handleAddPlayer} className="bg-purple-600 px-6 py-3 rounded text-white font-bold hover:bg-purple-500 whitespace-nowrap">Add Player / 添加</button>
            </div>
            
            <div className="flex gap-4 items-center flex-wrap">
              <button onClick={() => setShowBatchInput(!showBatchInput)} className="flex items-center gap-2 bg-zinc-800 px-4 py-2 rounded-lg text-white hover:bg-zinc-700 transition-colors text-sm">
                <ListPlus size={16} /> Batch Add / 批量添加
              </button>
              <input type="file" accept=".xlsx, .xls, .csv" ref={fileInputRef} onChange={handleFileUpload} className="hidden" />
              <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 bg-green-900/30 text-green-400 border border-green-500/50 px-4 py-2 rounded-lg hover:bg-green-900/50 transition-colors text-sm">
                <FileSpreadsheet size={16} /> Import Excel / 导入表格
              </button>
              <button
                onClick={handleDeleteSelectedPlayers}
                disabled={selectedCompetitorIds.length === 0}
                className="flex items-center gap-2 bg-yellow-900/30 text-yellow-400 border border-yellow-500/50 px-4 py-2 rounded-lg hover:bg-yellow-900/50 transition-colors text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Delete Selected / 删除所选 ({selectedCompetitorIds.length})
              </button>
              <button
                onClick={handleDeleteAllPlayers}
                disabled={room.competitors.length === 0}
                className="flex items-center gap-2 bg-red-900/30 text-red-400 border border-red-500/50 px-4 py-2 rounded-lg hover:bg-red-900/50 transition-colors text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Delete All / 全部删除
              </button>
            </div>
            <div className="text-sm text-zinc-400 bg-zinc-900/40 border border-zinc-800 rounded-lg px-4 py-3 leading-6">
              <div>Import rules: supports `.xlsx` / `.xls` / `.csv`; reads only the first sheet; column A is player number, column B is player name; if the player number is empty, the system assigns numbers in order automatically; if duplicate numbers exist, letters are appended automatically such as `31A`, `31B`; ignores empty rows; automatically skips common headers such as `No`, `编号`, `Name`, `姓名`.</div>
              <div>导入规则：支持 `.xlsx` / `.xls` / `.csv`；只读取第一个工作表；第一列为选手编号，第二列为选手姓名；如果选手编号为空，系统会按顺序自动补编号；如果编号重复，会自动在编号后追加字母区分，例如 `31A`、`31B`；空行会自动忽略；`No`、`编号`、`Name`、`姓名` 等常见表头会自动跳过。</div>
            </div>
            {room.competitors.length > 0 && (
              <div className="text-sm text-zinc-400">
                Selected / 已选择: <span className="text-white font-bold">{selectedCompetitorIds.length}</span> / {room.competitors.length}
              </div>
            )}

            {showBatchInput && (
              <div className="flex flex-col gap-3 bg-zinc-900/50 p-4 rounded-xl border border-zinc-800 animate-in fade-in slide-in-from-top-2">
                <textarea 
                  placeholder="Enter names separated by commas or newlines...&#10;输入选手名字，用逗号或换行分隔..." 
                  value={batchNames} 
                  onChange={e => setBatchNames(e.target.value)}
                  className="w-full bg-black/50 border border-zinc-700 rounded p-3 text-white focus:border-purple-500 outline-none min-h-[100px] font-rajdhani custom-scrollbar"
                />
                <div className="flex justify-end gap-3">
                  <button onClick={() => setShowBatchInput(false)} className="px-4 py-2 rounded text-zinc-400 hover:bg-zinc-800 text-sm">Cancel / 取消</button>
                  <button onClick={handleBatchAdd} className="bg-purple-600 px-4 py-2 rounded text-white font-bold hover:bg-purple-500 text-sm">Confirm Batch Add / 确认批量添加</button>
                </div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {room.competitors.map(c => (
              <div
                key={c.id}
                className={`bg-zinc-800 p-4 rounded border transition-colors ${selectedCompetitorIds.includes(c.id) ? 'border-purple-500 bg-purple-900/20' : 'border-zinc-700'}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <label className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedCompetitorIds.includes(c.id)}
                      onChange={() => handleToggleCompetitorSelection(c.id)}
                      className="h-4 w-4 accent-purple-500 shrink-0"
                    />
                    <div className="min-w-0 flex-1 pr-2">
                      {c.playerNumber && (
                        <div className="text-xs text-cyan-400 font-orbitron truncate">No. {c.playerNumber}</div>
                      )}
                      <div className="font-rajdhani text-lg truncate">{c.name}</div>
                    </div>
                  </label>
                  <button onClick={() => handleRemovePlayer(c.id)} className="text-red-500 hover:text-red-400 shrink-0"><X size={20}/></button>
                </div>
              </div>
            ))}
          </div>
          {room.competitors.length > 0 && (
            <button onClick={handleStartPrelims} className="mt-8 bg-green-600 w-full py-4 rounded-xl text-xl font-orbitron font-bold hover:bg-green-500">Start Preliminaries / 开始初赛</button>
          )}
        </div>
      </div>
    );
  }

  // Render Prelims
  if (room.status === 'PRELIM') {
    const rankedComps = calculatePrelimResults();
    const finalists = rankedComps.filter(c => c.isFinalist);
    const isFinalsReady = finalsAssignments.every(id => Boolean(id));
    const { submittedJudgeCount, totalJudgeCount, allJudgesSubmitted } = getJudgeSubmissionProgress(room);
    const canCalculatePrelims = submittedJudgeCount > 0;
    const isScoreSheetDirty = Object.values(judgeScoreDrafts).some(draft => draft?.dirty);
    const getFinalsCompetitor = (slotIndex: number) => finalists.find(c => c.id === finalsAssignments[slotIndex]);

    if (showFinalsSetupScreen && isOwner && finalists.length >= 16) {
      return (
        <div className="h-full w-full overflow-y-auto custom-scrollbar">
          <div className="p-4 md:p-8 w-full max-w-7xl mx-auto flex flex-col gap-6 min-h-full relative">
            <button onClick={() => setShowFinalsSetupScreen(false)} className="absolute top-4 left-4 p-2 text-white/50 hover:text-white transition-colors z-10"><ArrowLeft size={24} /></button>
            <div className="pt-12 md:pt-0">
              <h2 className="text-3xl font-orbitron text-cyan-400 text-center">Finals Match Setup<br/><span className="text-xl text-cyan-400/80">决赛对阵设置</span></h2>
              <p className="text-sm text-zinc-400 text-center mt-3">Drag players between slots, or tap one slot and then another to swap on touch devices. / 可通过拖动调整对阵；在触屏设备上可先点一个位置，再点另一个位置进行交换。</p>
            </div>

            <div className="flex justify-center gap-3 flex-wrap">
              <button onClick={handleAutoFillFinalsAssignments} className="px-4 py-2 rounded-lg text-sm border border-cyan-500/40 text-cyan-300 bg-cyan-900/20 hover:bg-cyan-900/40 transition-colors">
                Auto Fill By Ranking / 按排名自动填充
              </button>
              <button onClick={() => setShowFinalsSetupScreen(false)} className="px-4 py-2 rounded-lg text-sm border border-zinc-700 text-zinc-300 bg-zinc-900/40 hover:bg-zinc-800 transition-colors">
                Done / 完成设置
              </button>
            </div>

            <div className="bg-zinc-900/80 p-6 rounded-2xl border border-zinc-700">
              <div className="flex items-center justify-between gap-3 mb-4">
                <h4 className="font-orbitron text-white text-lg">A/B Matchups / A组-B组对阵表</h4>
                <span className={`text-sm ${isFinalsReady ? 'text-green-400' : 'text-yellow-400'}`}>
                  {isFinalsReady ? 'Ready / 已完成' : 'Incomplete / 未完成'}
                </span>
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {[
                  { key: 'A', title: 'Group A / A组', matches: [0, 1, 2, 3] },
                  { key: 'B', title: 'Group B / B组', matches: [4, 5, 6, 7] }
                ].map(section => (
                  <div key={section.key} className="bg-black/30 border border-zinc-800 rounded-xl p-4">
                    <h5 className="text-lg font-orbitron text-cyan-300 mb-4">{section.title}</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {section.matches.map(matchIndex => {
                        const firstSlotIndex = matchIndex * 2;
                        const secondSlotIndex = firstSlotIndex + 1;
                        const slotACompetitor = getFinalsCompetitor(firstSlotIndex);
                        const slotBCompetitor = getFinalsCompetitor(secondSlotIndex);

                        return (
                          <div key={matchIndex} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4 flex flex-col gap-3">
                            <div className="text-sm font-orbitron text-zinc-400">Match {matchIndex + 1} / 对阵 {matchIndex + 1}</div>
                            {[firstSlotIndex, secondSlotIndex].map(slotIndex => {
                              const competitor = getFinalsCompetitor(slotIndex);
                              const isSelectedSlot = selectedFinalsSlotIndex === slotIndex;
                              return (
                                <div
                                  key={slotIndex}
                                  onClick={() => handleSelectFinalsSlot(slotIndex)}
                                  onDragOver={e => e.preventDefault()}
                                  onDrop={e => {
                                    e.preventDefault();
                                    const competitorId = e.dataTransfer.getData('text/plain');
                                    if (competitorId) {
                                      handleDropToFinalsSlot(competitorId, slotIndex);
                                      setSelectedFinalsSlotIndex(null);
                                    }
                                  }}
                                  className={`min-h-[88px] rounded-lg border-2 border-dashed p-3 flex items-center justify-between gap-3 transition-colors cursor-pointer ${isSelectedSlot ? 'border-cyan-400 bg-cyan-900/20 ring-2 ring-cyan-500/40' : competitor ? 'border-purple-500/50 bg-purple-900/20' : 'border-zinc-700 bg-black/30'}`}
                                >
                                  {competitor ? (
                                    <div
                                      draggable
                                      onDragStart={e => e.dataTransfer.setData('text/plain', competitor.id)}
                                      className="min-w-0 flex-1 cursor-grab active:cursor-grabbing"
                                    >
                                      <div className="font-rajdhani text-xl text-white truncate">{competitor.name}</div>
                                      <div className="text-xs text-zinc-400">Seed #{competitor.prelimRank} / 排名 {competitor.prelimRank}</div>
                                      {isSelectedSlot && (
                                        <div className="text-xs text-cyan-300 mt-1">Selected, tap another slot to swap / 已选中，再点另一个位置交换</div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="text-sm text-zinc-500">Drop player here / 拖动选手到这里</div>
                                  )}
                                </div>
                              );
                            })}
                            <div className="text-xs text-zinc-500">
                              {slotACompetitor && slotBCompetitor ? `${slotACompetitor.name} VS ${slotBCompetitor.name}` : 'Waiting for both players / 等待两位选手'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="h-full w-full overflow-y-auto custom-scrollbar">
        <div className="p-4 md:p-8 w-full max-w-6xl mx-auto flex flex-col gap-6 min-h-full relative">
        <button onClick={() => navigate('/freestyle')} className="absolute top-4 left-4 p-2 text-white/50 hover:text-white transition-colors z-10"><ArrowLeft size={24} /></button>
        <h2 className="text-3xl font-orbitron text-purple-400 text-center mt-8 md:mt-0 mb-4">Preliminaries<br/><span className="text-xl text-purple-400/80">初赛阶段</span></h2>
        {roomLoadErrorMessage && (
          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
            {roomLoadErrorMessage}
          </div>
        )}
        
        <JudgesList />

        <div className="flex flex-col md:flex-row gap-8">
          {/* Left: Judge Panel */}
          <div className="flex-1 bg-zinc-900/80 p-6 rounded-2xl border border-zinc-700">
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <h2 className="text-2xl font-orbitron text-purple-400">Judge Panel - Preliminaries<br/><span className="text-lg text-purple-400/80">裁判面板 - 初赛</span></h2>
            <button
              onClick={handleManualRoomRefresh}
              disabled={isRefreshingRoom}
              className={`inline-flex w-fit shrink-0 whitespace-nowrap items-center justify-center gap-2 rounded-lg border px-4 py-2 text-xs font-orbitron uppercase tracking-[0.18em] transition-colors ${
                isRefreshingRoom
                  ? 'border-cyan-500/20 bg-cyan-500/10 text-cyan-300/50 cursor-not-allowed'
                  : 'border-cyan-500/40 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20'
              }`}
            >
              <RefreshCw size={14} className={isRefreshingRoom ? 'animate-spin' : ''} />
              {isRefreshingRoom ? t('refreshing') : t('refreshScores')}
            </button>
          </div>
          {user?.uid && (
            <div className="mb-6 rounded-xl border border-purple-500/20 bg-black/40 p-4 flex flex-col gap-3">
              <div className="text-sm text-zinc-300">
                Submit the full score sheet after finishing all competitors. / 请在完成所有选手评分后一次性提交整张评分表。
              </div>
              <button
                type="button"
                onClick={handleScoreSheetSubmit}
                disabled={isSubmittingScoreSheet || room.competitors.length === 0}
                className="rounded-xl bg-purple-600 px-6 py-3 font-bold transition-all hover:bg-purple-500 active:scale-[0.98] active:bg-purple-400 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmittingScoreSheet
                  ? 'Submitting Score Sheet / 提交评分表中'
                  : isScoreSheetDirty
                    ? 'Submit Full Score Sheet / 提交整张评分表'
                    : 'Resubmit Full Score Sheet / 重新提交整张评分表'}
              </button>
            </div>
          )}
          <div className="flex flex-col gap-4">
            {judgePanelCompetitors.map(comp => {
              const myScore = comp.prelimScores?.[user.uid];
              const isSelected = currentPlayerId === comp.id;
              const draft = judgeScoreDrafts[comp.id] || getDraftFromScore(myScore);
              const draftTotal = draft.style + draft.completeness + draft.difficulty;
              
              return (
                <div key={comp.id} className={`p-4 rounded-xl border transition-all ${isSelected ? 'border-purple-500 bg-purple-900/20' : 'border-zinc-700 bg-black/40'}`}>
                  <div className="flex justify-between items-center cursor-pointer" onClick={() => handleSelectCompetitorForScoring(comp)}>
                    <div className="min-w-0">
                      {comp.playerNumber ? (
                        <div className="text-xs font-orbitron uppercase tracking-[0.18em] text-cyan-300">
                          No. {comp.playerNumber}
                        </div>
                      ) : null}
                      <span className="text-xl font-rajdhani font-bold block truncate">{comp.name}</span>
                    </div>
                    {draft.dirty ? (
                      <span className="text-amber-400 flex items-center gap-2"><AlertCircle size={18}/> 草稿: {draftTotal}</span>
                    ) : myScore ? (
                      <span className="text-green-400 flex items-center gap-2"><Check size={18}/> 已打分: {myScore.total}</span>
                    ) : (
                      <span className="text-yellow-500 flex items-center gap-2"><AlertCircle size={18}/> 待打分</span>
                    )}
                  </div>
                  
                  {isSelected && (
                    <div className="mt-6 flex flex-col gap-4 bg-black/50 p-4 rounded-lg">
                      <div className="flex flex-col gap-2">
                        <label className="text-sm text-zinc-400 flex justify-between">
                          <span>Style / 风格 (0-30)</span>
                          <span className="text-purple-400 font-bold">{draft.style}</span>
                        </label>
                        <input type="range" min="0" max="30" value={draft.style} onChange={e => handleScoreDraftChange(comp.id, 'style', parseInt(e.target.value, 10))} className="accent-purple-500"/>
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-sm text-zinc-400 flex justify-between">
                          <span>Completeness / 完整度 (0-40)</span>
                          <span className="text-blue-400 font-bold">{draft.completeness}</span>
                        </label>
                        <input type="range" min="0" max="40" value={draft.completeness} onChange={e => handleScoreDraftChange(comp.id, 'completeness', parseInt(e.target.value, 10))} className="accent-blue-500"/>
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-sm text-zinc-400 flex justify-between">
                          <span>Difficulty / 难度 (0-30)</span>
                          <span className="text-red-400 font-bold">{draft.difficulty}</span>
                        </label>
                        <input type="range" min="0" max="30" value={draft.difficulty} onChange={e => handleScoreDraftChange(comp.id, 'difficulty', parseInt(e.target.value, 10))} className="accent-red-500"/>
                      </div>
                      <div className="flex justify-between items-center mt-4 pt-4 border-t border-zinc-800 gap-4">
                        <span className="text-xl font-orbitron">Total / 总分: {draftTotal}</span>
                        <span className={`text-xs font-orbitron uppercase tracking-[0.16em] ${draft.dirty ? 'text-amber-300' : 'text-zinc-500'}`}>
                          {draft.dirty ? 'Draft Only / 仅草稿' : 'Saved / 已保存'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Scoreboard */}
        <div className="w-full md:w-80 bg-zinc-900/80 p-6 rounded-2xl border border-zinc-700 flex flex-col mt-4 md:mt-0">
          <h2 className="text-2xl font-orbitron text-yellow-400 mb-6 flex items-center gap-2"><Trophy/> Leaderboard / 排行榜</h2>
          <div className="mb-4 text-xs text-zinc-400 bg-black/30 border border-zinc-800 rounded-lg px-3 py-2">
            Judges Submitted / 裁判提交进度: {submittedJudgeCount} / {totalJudgeCount}
          </div>
          <div className="mb-4 text-xs text-zinc-400 bg-black/30 border border-zinc-800 rounded-lg px-3 py-2">
            {hasCalculatedPrelimResults
              ? 'Averages calculated by chief judge / 主裁已完成平均分计算'
              : allJudgesSubmitted
                ? 'All judges submitted. Waiting for chief judge to calculate. / 所有裁判已提交，等待主裁计算平均分'
                : canCalculatePrelims
                  ? 'Partial judge submissions received. Chief judge can calculate using current submitted scores. / 已有部分裁判提交，主裁可按当前已提交分数计算平均分'
                  : 'Waiting for at least one judge to submit a full score sheet. / 等待至少一位裁判提交整张评分表'}
          </div>
          <div className="flex flex-col gap-2 flex-1" key={`leaderboard-${room.revision ?? 0}-${room.prelimResultsCalculatedAt ?? 0}`}>
            {rankedComps.map((comp, idx) => (
              <div
                key={`${comp.id}:${comp.prelimRank ?? 'pending'}:${comp.prelimAverage}:${comp._scoreCount}`}
                className={`flex justify-between items-center p-3 rounded border ${hasCalculatedPrelimResults && idx < 16 ? 'border-yellow-500/50 bg-yellow-900/10' : 'border-zinc-800 bg-black/40 text-zinc-500'}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-orbitron font-bold w-6">{hasCalculatedPrelimResults ? (comp.prelimRank ?? idx + 1) : idx + 1}.</span>
                  <div className="min-w-0">
                    {comp.playerNumber ? (
                      <div className="text-[10px] font-orbitron uppercase tracking-[0.18em] text-cyan-300 truncate">
                        No. {comp.playerNumber}
                      </div>
                    ) : null}
                    <span className="font-rajdhani truncate block w-24" title={comp.name}>{comp.name}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <span className="font-orbitron text-lg leading-none">{hasCalculatedPrelimResults ? comp.prelimAverage.toFixed(1) : '--'}</span>
                  <span className="text-[10px] text-zinc-500">
                    {comp._scoreCount}/{totalJudgeCount} 裁判
                  </span>
                  <span className={`text-[10px] ${hasCalculatedPrelimResults ? 'text-green-400' : comp._isScoreComplete ? 'text-cyan-300' : 'text-yellow-500'}`}>
                    {hasCalculatedPrelimResults ? '已计算' : comp._isScoreComplete ? '已提交' : '待提交'}
                  </span>
                </div>
              </div>
            ))}
          </div>
          
          <button
            onClick={handleExportPrelimDetailedRankings}
            disabled={!hasCalculatedPrelimResults}
            className="mt-4 w-full bg-green-700 py-3 rounded-lg font-orbitron font-bold hover:bg-green-600 text-sm flex justify-center items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FileSpreadsheet size={18} /> Export Detailed Ranking Excel / 导出详细排名Excel
          </button>

          {isOwner && (
            <>
              <button
                onClick={handleCalculatePrelims}
                disabled={!canCalculatePrelims || isCalculatingPrelims}
                className="mt-6 w-full bg-yellow-600 py-3 rounded-lg font-orbitron font-bold hover:bg-yellow-500 text-sm flex justify-center items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isCalculatingPrelims ? 'Calculating / 计算中' : 'Calculate Prelim Averages / 计算初赛平均分'}
              </button>
              {finalists.length >= 16 && (
                <button onClick={() => setShowFinalsSetupScreen(true)} className="mt-6 w-full bg-cyan-700 py-3 rounded-lg font-orbitron font-bold hover:bg-cyan-600 text-sm flex justify-center items-center gap-2">
                  Open Finals Match Setup / 打开决赛对阵设置
                </button>
              )}
              <button onClick={handleStartFinals} disabled={!isFinalsReady || !hasCalculatedPrelimResults} className="mt-3 w-full bg-green-600 py-3 rounded-lg font-orbitron font-bold hover:bg-green-500 text-sm flex justify-center items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
                Generate Finals Bracket / 生成决赛对阵 <ArrowRight size={18}/>
              </button>
            </>
          )}
        </div>
        </div>
        </div>
      </div>
    );
  }

  // Render Finals (Tournament Bracket & Voting)
  if (room.status === 'FINALS') {
      const getCompetitor = (id: string) => room.competitors.find(c => c.id === id);
      const activeMatch = sortMatchesBySequence(room.matches)
        .filter(m => !m.winnerId && m.competitorAId && m.competitorBId)
        [0];
      const activeMatchMeta = activeMatch ? getFinalsMatchMeta(activeMatch) : null;
      const completedMatches = room.matches.filter(m => m.competitorAId && m.competitorBId);
      const isFinalsComplete = completedMatches.length > 0 && completedMatches.every(m => Boolean(m.winnerId));
      const placements = getFinalsPlacements(room);

      return (
        <div className="p-4 md:p-8 w-full max-w-6xl mx-auto flex flex-col h-full gap-6 overflow-y-auto custom-scrollbar relative">
            <button onClick={() => navigate('/freestyle')} className="absolute top-4 left-4 p-2 text-white/50 hover:text-white transition-colors z-10"><ArrowLeft size={24} /></button>
            <h2 className="text-3xl font-orbitron text-purple-400 text-center mt-8 md:mt-0">Finals Bracket<br/><span className="text-xl text-purple-400/80">决赛对阵树状图</span></h2>
            
            <JudgesList />

            {isOwner && (
                <div className="flex justify-center gap-3 flex-wrap mb-4">
                    <button onClick={handleReturnToPrelims} className="bg-red-700 px-4 py-2 rounded-lg font-orbitron font-bold hover:bg-red-600 text-sm">
                        Back To Prelims / 返回初赛修改
                    </button>
                    <button onClick={handleStartFinals} className="bg-yellow-600 px-4 py-2 rounded-lg font-orbitron font-bold hover:bg-yellow-500 text-sm">
                        Regenerate Bracket / 重新生成对阵树
                    </button>
                </div>
            )}

            {isOwner && (
                <div className="max-w-4xl mx-auto w-full text-xs text-cyan-300 bg-cyan-900/10 border border-cyan-500/30 rounded-xl px-4 py-3">
                    Chief Referee Controls: each match card below now supports correcting the winner, and the bracket will recalculate automatically. / 主裁判功能：下方每个对决卡片都可以直接改判胜者，系统会自动重算后续对阵与名次。
                </div>
            )}

            {/* Active Match Voting Panel */}
            {activeMatch && isJudge && (
                <div className="bg-purple-900/20 border border-purple-500 p-6 rounded-2xl max-w-2xl mx-auto w-full flex flex-col items-center gap-6">
                    <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <h3 className="text-2xl font-orbitron text-white text-center sm:text-left">Vote for Winner / 投票选择胜者</h3>
                      <button
                        onClick={handleManualRoomRefresh}
                        disabled={isRefreshingRoom}
                        className={`inline-flex items-center justify-center gap-2 self-center rounded-lg border px-4 py-2 text-xs font-orbitron uppercase tracking-[0.18em] transition-colors ${
                          isRefreshingRoom
                            ? 'border-cyan-500/20 bg-cyan-500/10 text-cyan-300/50 cursor-not-allowed'
                            : 'border-cyan-500/40 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20'
                        }`}
                      >
                        <RefreshCw size={14} className={isRefreshingRoom ? 'animate-spin' : ''} />
                        {isRefreshingRoom ? t('refreshing') : t('refreshVotes')}
                      </button>
                    </div>
                    <p className="text-zinc-400 text-center font-rajdhani text-sm">
                      {activeMatchMeta?.group === 'A' && 'Group A / A组'}
                      {activeMatchMeta?.group === 'B' && 'Group B / B组'}
                      {activeMatchMeta?.group === 'Final' && 'Championship / 冠亚军赛'}
                      {activeMatchMeta?.group === 'Placement' && '3rd Place Match / 季军赛'}
                    </p>
                    <p className="text-zinc-500 text-center font-rajdhani text-xs">{activeMatchMeta?.stage}</p>
                    
                    <div className="flex justify-between w-full gap-4">
                        <button 
                            onClick={() => handleVoteSubmit(activeMatch.id, activeMatch.competitorAId)}
                            className={`flex-1 py-6 rounded-xl border-2 transition-all flex flex-col items-center justify-center gap-2
                                ${activeMatch.votes[user.uid] === activeMatch.competitorAId ? 'border-purple-500 bg-purple-600/50' : 'border-zinc-700 bg-black/50 hover:border-purple-400'}`}
                        >
                            <span className="text-2xl font-rajdhani font-bold">{getCompetitor(activeMatch.competitorAId)?.name}</span>
                            <span className="text-sm text-zinc-400">Player A / 选手A</span>
                        </button>
                        
                        <div className="flex items-center justify-center text-zinc-600 font-orbitron font-black text-2xl px-2">VS</div>
                        
                        <button 
                            onClick={() => handleVoteSubmit(activeMatch.id, activeMatch.competitorBId)}
                            className={`flex-1 py-6 rounded-xl border-2 transition-all flex flex-col items-center justify-center gap-2
                                ${activeMatch.votes[user.uid] === activeMatch.competitorBId ? 'border-blue-500 bg-blue-600/50' : 'border-zinc-700 bg-black/50 hover:border-blue-400'}`}
                        >
                            <span className="text-2xl font-rajdhani font-bold">{getCompetitor(activeMatch.competitorBId)?.name}</span>
                            <span className="text-sm text-zinc-400">Player B / 选手B</span>
                        </button>
                    </div>

                    {isOwner && (
                        <div className="w-full mt-4 pt-4 border-t border-purple-500/30 flex flex-col gap-3">
                            <div className="flex justify-between text-sm text-zinc-300">
                                <span>Votes A / 票数A: {Object.values(activeMatch.votes).filter(v => v === activeMatch.competitorAId).length}</span>
                                <span>Votes B / 票数B: {Object.values(activeMatch.votes).filter(v => v === activeMatch.competitorBId).length}</span>
                                <span>Total / 总票数: {Object.keys(activeMatch.votes).length} / {room.judges.length}</span>
                            </div>
                            <div className="flex gap-4">
                                <button onClick={() => advanceMatch(activeMatch.id, activeMatch.competitorAId)} className="flex-1 bg-purple-700 py-2 rounded font-bold hover:bg-purple-600 text-sm">Advance A / 晋级A</button>
                                <button onClick={() => advanceMatch(activeMatch.id, activeMatch.competitorBId)} className="flex-1 bg-blue-700 py-2 rounded font-bold hover:bg-blue-600 text-sm">Advance B / 晋级B</button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {isFinalsComplete && (
                <div className="bg-green-900/20 border border-green-500/40 p-6 rounded-2xl max-w-4xl mx-auto w-full flex flex-col items-center gap-4">
                    <h3 className="text-2xl font-orbitron text-green-400 text-center">Finals Completed / 决赛已结束</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 w-full">
                        <div className="bg-black/40 border border-yellow-500/30 rounded-xl p-4 text-center">
                            <div className="text-xs text-yellow-400 font-orbitron">Champion / 冠军</div>
                            <div className="text-lg font-rajdhani text-white mt-2">{placements.champion?.name || 'TBD / 待定'}</div>
                        </div>
                        <div className="bg-black/40 border border-zinc-500/30 rounded-xl p-4 text-center">
                            <div className="text-xs text-zinc-300 font-orbitron">Runner-up / 亚军</div>
                            <div className="text-lg font-rajdhani text-white mt-2">{placements.runnerUp?.name || 'TBD / 待定'}</div>
                        </div>
                        <div className="bg-black/40 border border-amber-700/40 rounded-xl p-4 text-center">
                            <div className="text-xs text-amber-400 font-orbitron">3rd Place / 季军</div>
                            <div className="text-lg font-rajdhani text-white mt-2">{placements.thirdPlace?.name || 'TBD / 待定'}</div>
                        </div>
                        <div className="bg-black/40 border border-zinc-700 rounded-xl p-4 text-center">
                            <div className="text-xs text-zinc-400 font-orbitron">4th Place / 第四名</div>
                            <div className="text-lg font-rajdhani text-white mt-2">{placements.fourthPlace?.name || 'TBD / 待定'}</div>
                        </div>
                    </div>
                    {isOwner && (
                        <button onClick={handleGoToRoomLobby} className="bg-green-600 px-5 py-3 rounded-xl font-orbitron font-bold hover:bg-green-500 text-sm">
                            Go To Room Lobby / 返回房间大厅
                        </button>
                    )}
                </div>
            )}

            {/* Basic Bracket Visualization */}
            <div className="flex flex-col gap-8 mt-8">
                {[1, 2, 3, 4].map(roundNum => {
                    const roundMatches = sortMatchesBySequence(room.matches.filter(m => m.round === roundNum));
                    if (roundMatches.length === 0) return null;
                    
                    const roundNames = ['Round of 16 / 十六强', 'Group Semifinals / 小组半决赛', 'Group Finals / 小组决赛', 'Placement Finals / 名次赛'];
                    return (
                        <div key={roundNum} className="flex flex-col gap-4">
                            <h4 className="text-xl font-orbitron text-zinc-500 border-b border-zinc-800 pb-2">{roundNames[roundNum - 1]}</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                {roundMatches.map(match => {
                                    const matchMeta = getFinalsMatchMeta(match);
                                    return (
                                    <div key={match.id} className={`p-3 rounded-lg border flex flex-col gap-2 ${match.winnerId ? 'border-zinc-800 bg-black/20' : 'border-purple-500/50 bg-purple-900/10'}`}>
                                        {match.round <= 3 && (
                                            <div className="text-[10px] text-cyan-300 text-center font-orbitron bg-cyan-900/20 py-1 rounded">
                                              {matchMeta.group === 'A' ? 'Group A / A组' : 'Group B / B组'}
                                            </div>
                                        )}
                                        {match.round === 4 && match.position === 1 && (
                                            <div className="text-xs text-yellow-500 text-center font-orbitron bg-yellow-900/20 py-1 rounded">3rd Place Match / 季军赛</div>
                                        )}
                                        {match.round === 4 && match.position === 0 && (
                                            <div className="text-xs text-yellow-400 text-center font-orbitron bg-yellow-900/20 py-1 rounded">Championship / 冠亚军赛</div>
                                        )}
                                        <div className={`flex justify-between items-center ${match.winnerId === match.competitorAId ? 'text-green-400 font-bold' : 'text-zinc-400'}`}>
                                            <span className="truncate">{getCompetitor(match.competitorAId)?.name || 'TBD / 待定'}</span>
                                            {match.winnerId === match.competitorAId && <Check size={14}/>}
                                        </div>
                                        <div className={`flex justify-between items-center ${match.winnerId === match.competitorBId ? 'text-green-400 font-bold' : 'text-zinc-400'}`}>
                                            <span className="truncate">{getCompetitor(match.competitorBId)?.name || 'TBD / 待定'}</span>
                                            {match.winnerId === match.competitorBId && <Check size={14}/>}
                                        </div>
                                        {isOwner && match.competitorAId && match.competitorBId && (
                                            <div className="mt-2 pt-2 border-t border-zinc-800 flex flex-col gap-2">
                                                <div className="text-[10px] text-cyan-300 font-orbitron text-center">Chief Referee Override / 主裁改判</div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <button
                                                      onClick={() => handleOverrideMatchWinner(match.id, match.competitorAId)}
                                                      className={`px-2 py-2 rounded text-[11px] font-bold transition-colors ${match.winnerId === match.competitorAId ? 'bg-purple-700 text-white' : 'bg-zinc-800 text-zinc-200 hover:bg-purple-900/60'}`}
                                                    >
                                                      Set {getCompetitor(match.competitorAId)?.name || 'A'}
                                                    </button>
                                                    <button
                                                      onClick={() => handleOverrideMatchWinner(match.id, match.competitorBId)}
                                                      className={`px-2 py-2 rounded text-[11px] font-bold transition-colors ${match.winnerId === match.competitorBId ? 'bg-blue-700 text-white' : 'bg-zinc-800 text-zinc-200 hover:bg-blue-900/60'}`}
                                                    >
                                                      Set {getCompetitor(match.competitorBId)?.name || 'B'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )})}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
      );
  }

  if (room.status === 'CLOSED') {
    const rankedComps = calculatePrelimResults();
    const totalJudgeCount = room.judges.length;
    const getCompetitor = (id: string) => room.competitors.find(c => c.id === id);
    const placements = getFinalsPlacements(room);

    return (
      <div className="h-full w-full overflow-y-auto custom-scrollbar">
        <div className="p-4 md:p-8 w-full max-w-6xl mx-auto flex flex-col gap-6 min-h-full relative">
          <button onClick={() => navigate('/freestyle')} className="absolute top-4 left-4 p-2 text-white/50 hover:text-white transition-colors z-10"><ArrowLeft size={24} /></button>
          <div className="pt-12 md:pt-0 text-center">
            <h2 className="text-3xl font-orbitron text-cyan-400">Room Lobby: {room.name}<br/><span className="text-xl text-cyan-400/80">房间大厅</span></h2>
            <p className="text-zinc-400 mt-3 text-sm">Export prelim rankings and the finals detail image from this room lounge. / 可在此导出初赛排名表与决赛详情图。</p>
          </div>

          <JudgesList />

          <div className="flex flex-wrap justify-center gap-3">
            <button onClick={handleExportPrelimRankings} className="flex items-center gap-2 bg-green-900/30 text-green-400 border border-green-500/50 px-4 py-2 rounded-lg hover:bg-green-900/50 transition-colors text-sm">
              <FileSpreadsheet size={16} /> Export Prelim Rankings / 导出初赛排名
            </button>
            <button onClick={handleExportFinalsDetailsImage} className="bg-cyan-900/30 text-cyan-300 border border-cyan-500/50 px-4 py-2 rounded-lg hover:bg-cyan-900/50 transition-colors text-sm">
              Export Finals Detail Image / 导出决赛详情图
            </button>
          </div>

          <div ref={finalsDetailsRef} className="bg-zinc-900/80 p-6 rounded-2xl border border-zinc-700 flex flex-col gap-6">
            <div className="text-center">
              <h3 className="text-3xl font-orbitron text-white">{room.name}</h3>
              <p className="text-sm text-zinc-400 mt-2">Freestyle Finals Summary / 自由式决赛总结</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="bg-gradient-to-b from-zinc-300/12 to-black/40 border border-zinc-300/30 rounded-2xl p-5 text-center md:order-1">
                <div className="text-[11px] text-zinc-300 font-orbitron tracking-wider">Runner-up / 亚军</div>
                <div className="text-4xl font-orbitron text-zinc-200 mt-3">2</div>
                <div className="text-xl font-rajdhani text-white mt-3 break-words">{placements.runnerUp?.name || 'TBD / 待定'}</div>
              </div>
              <div className="bg-gradient-to-b from-yellow-300/20 to-black/40 border border-yellow-400/40 rounded-2xl p-6 text-center shadow-[0_0_30px_rgba(250,204,21,0.12)] md:order-2 md:-translate-y-3">
                <div className="text-xs text-yellow-300 font-orbitron tracking-[0.2em]">Champion / 冠军</div>
                <div className="text-5xl font-orbitron text-yellow-300 mt-3">1</div>
                <div className="text-2xl font-rajdhani text-white mt-3 break-words">{placements.champion?.name || 'TBD / 待定'}</div>
              </div>
              <div className="bg-gradient-to-b from-amber-700/20 to-black/40 border border-amber-600/35 rounded-2xl p-5 text-center md:order-3">
                <div className="text-[11px] text-amber-400 font-orbitron tracking-wider">3rd Place / 季军</div>
                <div className="text-4xl font-orbitron text-amber-400 mt-3">3</div>
                <div className="text-xl font-rajdhani text-white mt-3 break-words">{placements.thirdPlace?.name || 'TBD / 待定'}</div>
              </div>
            </div>

            <div className="bg-black/30 border border-zinc-800 rounded-xl p-4">
              <h4 className="text-xl font-orbitron text-yellow-400 mb-4">Prelim Top 16 / 初赛前十六</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {rankedComps.slice(0, 16).map((comp: any) => (
                  <div key={comp.id} className="flex justify-between items-center p-3 rounded border border-zinc-800 bg-zinc-950/60">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="font-orbitron font-bold w-6">{comp.prelimRank}.</span>
                      <span className="font-rajdhani truncate">{comp.name}</span>
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="font-orbitron text-sm text-zinc-300">{comp.prelimAverage.toFixed(1)}</span>
                      <span className="text-[10px] text-zinc-500">{comp._scoreCount}/{totalJudgeCount}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-8">
              {[1, 2, 3, 4].map(roundNum => {
                const roundMatches = sortMatchesBySequence(room.matches.filter(m => m.round === roundNum));
                if (roundMatches.length === 0) return null;

                const roundNames = ['Round of 16 / 十六强', 'Group Semifinals / 小组半决赛', 'Group Finals / 小组决赛', 'Placement Finals / 名次赛'];
                return (
                  <div key={roundNum} className="flex flex-col gap-4">
                    <h4 className="text-xl font-orbitron text-zinc-400 border-b border-zinc-800 pb-2">{roundNames[roundNum - 1]}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {roundMatches.map(match => {
                        const matchMeta = getFinalsMatchMeta(match);
                        return (
                        <div key={match.id} className={`p-3 rounded-lg border flex flex-col gap-2 ${match.winnerId ? 'border-zinc-800 bg-black/20' : 'border-purple-500/50 bg-purple-900/10'}`}>
                          {match.round <= 3 && (
                            <div className="text-[10px] text-cyan-300 text-center font-orbitron bg-cyan-900/20 py-1 rounded">
                              {matchMeta.group === 'A' ? 'Group A / A组' : 'Group B / B组'}
                            </div>
                          )}
                          {match.round === 4 && match.position === 1 && (
                            <div className="text-xs text-yellow-500 text-center font-orbitron bg-yellow-900/20 py-1 rounded">3rd Place Match / 季军赛</div>
                          )}
                          {match.round === 4 && match.position === 0 && (
                            <div className="text-xs text-yellow-400 text-center font-orbitron bg-yellow-900/20 py-1 rounded">Championship / 冠亚军赛</div>
                          )}
                          <div className={`flex justify-between items-center ${match.winnerId === match.competitorAId ? 'text-green-400 font-bold' : 'text-zinc-400'}`}>
                            <span className="truncate">{getCompetitor(match.competitorAId)?.name || 'TBD / 待定'}</span>
                            {match.winnerId === match.competitorAId && <Check size={14}/>}
                          </div>
                          <div className={`flex justify-between items-center ${match.winnerId === match.competitorBId ? 'text-green-400 font-bold' : 'text-zinc-400'}`}>
                            <span className="truncate">{getCompetitor(match.competitorBId)?.name || 'TBD / 待定'}</span>
                            {match.winnerId === match.competitorBId && <Check size={14}/>}
                          </div>
                        </div>
                      )})}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
